import type { UploadItem, UploadProps } from "@arco-design/web-react";
import { useEffect, useRef, useState } from "react";

import { isHttpClientError, resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import { uploadMedia } from "../../media/repo";
import { CONTACT_RECORD_IMAGE_MAX_BYTES, CONTACT_RECORD_VIDEO_MAX_BYTES } from "../config";
import type { ContactWorkbenchRecordAttachment } from "../types";

/** 管理动态附件的进度、失败恢复与卸载取消，成功结果按文件列表顺序写入草稿。 */
export function useContactRecordUpload(
  attachments: ContactWorkbenchRecordAttachment[],
  onAttachmentsChange: (attachments: ContactWorkbenchRecordAttachment[]) => void,
) {
  const [files, setFiles] = useState<UploadItem[]>(() => attachments.map((item, index) => ({
    uid: `saved-${index}`, name: item.content, url: item.fileUrl, status: "done",
  })));
  const controllers = useRef(new Set<AbortController>());
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    const requests = controllers.current;
    return () => {
      active.current = false;
      requests.forEach((controller) => controller.abort());
      requests.clear();
    };
  }, []);

  const onChange: UploadProps["onChange"] = (nextFiles) => {
    if (!active.current) return;
    const updatedFiles = nextFiles.map((item) => ({
      ...item,
      // 文本列表无需生成大文件本地预览，成功后才使用服务端公开地址。
      url: item.status === "done" ? (item.response?.fileUrl ?? item.url) : "",
    }));
    setFiles(updatedFiles);
    onAttachmentsChange(updatedFiles.filter((item) => item.status === "done" && item.url).map((item) => ({
      fileUrl: item.url!, content: item.name,
    })));
  };

  const customRequest: UploadProps["customRequest"] = ({ file, onProgress, onSuccess, onError }) => {
    if (!active.current) return;
    const controller = new AbortController();
    controllers.current.add(controller);
    void (async () => {
      try {
        const validationError = validateMediaFile(file);
        if (validationError) throw new Error(validationError);
        const result = await uploadMedia(file, file.type.startsWith("image/") ? "image" : "file", {
          purpose: "contact_record",
          signal: controller.signal,
          onProgress: (percent) => {
            if (!controller.signal.aborted) onProgress(percent);
          },
        });
        if (!controller.signal.aborted) onSuccess(result);
      } catch (error) {
        if (!controller.signal.aborted) onError({ message: resolveUploadError(error) });
      } finally {
        controllers.current.delete(controller);
      }
    })();
    return { abort: () => controller.abort() };
  };

  return {
    files,
    uploading: files.some((file) => file.status === "init" || file.status === "uploading"),
    failed: files.filter((file) => file.status === "error"),
    uploadProps: {
      fileList: files,
      customRequest,
      onChange,
      onProgress: (file) => setFiles((current) => current.map((item) => item.uid === file.uid ? { ...file } : item)),
    } satisfies Pick<UploadProps, "fileList" | "customRequest" | "onChange" | "onProgress">,
  };
}

/** 文件大小边界与后端一致，等于上限时允许上传。 */
function validateMediaFile(file: File): string | null {
  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");
  if (!isImage && !isVideo) return "只能上传图片或视频文件";
  if (isImage && file.size > CONTACT_RECORD_IMAGE_MAX_BYTES) return "图片大小不能超过 20MB";
  if (isVideo && file.size > CONTACT_RECORD_VIDEO_MAX_BYTES) return "视频大小不能超过 150MB";
  return null;
}

/** 将超时、请求体超限及业务失败转换为可持续展示的上传提示。 */
function resolveUploadError(error: unknown): string {
  if (isHttpClientError(error)) {
    if (error.statusCode === 413) return "文件超过服务器上传限制，请缩小文件后重新选择";
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return "上传超时，请检查网络后重试";
    if (error.statusCode === null) return "网络连接失败，请检查网络后重试";
  }
  return resolveReadableHttpErrorMessage(error, "图片/视频上传失败，请重试");
}
